import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  LucideX,
  LucideEye,
  LucideCheck,
  LucideClipboardList,
  LucideClock,
  LucideFilter,
} from "lucide-react";
import { REPORT_TYPES } from "@/constants/reportTypes";
import { Modal } from "@/components/modals/Modal";
import { useSchool } from "@/context/SchoolContext";

const GRADE_OPTIONS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

function getSchoolYears() {
  const y = new Date().getFullYear();
  return [`${y - 1}–${y}`, `${y}–${y + 1}`, `${y + 1}–${y + 2}`];
}

const nativeSelect =
  "h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm " +
  "ring-offset-background transition-colors focus:outline-none focus:ring-2 " +
  "focus:ring-ring focus:ring-offset-2 text-foreground";

export function CreateClassModal({ open, onClose, onSubmit, creating }) {
  const { memberships } = useSchool();
  const schoolId = memberships[0]?.school?._id ?? "";

  const [formData, setFormData] = useState({
    name: "",
    grade: "",
    schoolYear: "",
    reportType: "",
    schoolId,
  });

  useEffect(() => {
    if (open) setFormData((p) => ({ ...p, schoolId }));
  }, [open, schoolId]);
  const [previewPdf, setPreviewPdf] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const typeListRef = useRef(null);

  const schoolYears = getSchoolYears();

  function handleGradeChange(grade) {
    const gNum = grade ? parseInt(grade) : null;
    const nowVisible = gNum
      ? REPORT_TYPES.filter((t) => t.grades.includes(gNum))
      : REPORT_TYPES;
    const currentStillValid =
      formData.reportType &&
      nowVisible.some((t) => t.value === formData.reportType && !t.disabled);
    setFormData((p) => ({
      ...p,
      grade,
      reportType: currentStillValid
        ? p.reportType
        : nowVisible.find((t) => !t.disabled)?.value ?? "",
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!formData.grade || !formData.schoolYear || !formData.reportType) return;
    await onSubmit(formData);
    setFormData({
      name: "",
      grade: "",
      schoolYear: "",
      reportType: "",
      schoolId: "",
    });
  }

  const gradeNum = formData.grade ? parseInt(formData.grade) : null;
  const visibleTypes = gradeNum
    ? REPORT_TYPES.filter((t) => t.grades.includes(gradeNum))
    : REPORT_TYPES;
  const availableCount = visibleTypes.filter((t) => !t.disabled).length;
  const canSubmit =
    !creating && formData.grade && formData.schoolYear && formData.reportType;

  return (
    <>
      {/* Entrance animation keyframes */}
      <style>{`
        @keyframes typeRowIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .type-row-enter {
          animation: typeRowIn 0.18s ease both;
        }
      `}</style>

      <Modal
        open={open}
        onClose={onClose}
        title="Create New Class"
        subtitle="Set up a class to start adding students and generating reports."
        icon={<LucideClipboardList size={16} className="text-primary" />}
        maxWidth="max-w-lg"
        maxHeight="max-h-[92vh]"
      >
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5 overflow-y-auto">
          {/* Grade + School Year row */}
          <div className="flex gap-3">
            <div className="space-y-1.5 shrink-0" style={{ width: 96 }}>
              <Label className="text-sm font-medium">
                Grade <span className="text-destructive/80 font-normal">*</span>
              </Label>
              <select
                value={formData.grade}
                onChange={(e) => handleGradeChange(e.target.value)}
                className={nativeSelect}
                required
              >
                <option value="">-</option>
                {GRADE_OPTIONS.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5 flex-1">
              <Label className="text-sm font-medium">
                School Year{" "}
                <span className="text-destructive/80 font-normal">*</span>
              </Label>
              <select
                value={formData.schoolYear}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, schoolYear: e.target.value }))
                }
                className={nativeSelect}
                required
              >
                <option value="">Select year</option>
                {schoolYears.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Class Name */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-foreground">
              Class Name{" "}
              <span className="text-muted-foreground font-normal text-xs">
                (optional)
              </span>
            </Label>
            <Input
              placeholder="e.g. Room 204"
              value={formData.name}
              onChange={(e) =>
                setFormData((p) => ({ ...p, name: e.target.value }))
              }
              className="h-10"
            />
          </div>

          {/* School (read-only, auto-filled) */}
          {memberships[0]?.school && (
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-foreground">School</Label>
              <div className={nativeSelect + " text-muted-foreground select-none"}>
                {memberships[0].school.name}
              </div>
            </div>
          )}

          {/* Report Type */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">
                Report Type{" "}
                <span className="text-destructive/80 font-normal">*</span>
              </Label>
              {gradeNum && (
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted rounded-full px-2 py-0.5">
                  <LucideFilter size={10} />
                  {availableCount} available for Grade {formData.grade}
                </span>
              )}
            </div>

            {!gradeNum && (
              <p className="text-xs text-muted-foreground/70 italic">
                Select a grade above to see only compatible report types.
              </p>
            )}

            <div ref={typeListRef} className="space-y-2">
              {visibleTypes.map((type, i) => (
                <ReportTypeRow
                  key={type.value}
                  type={type}
                  index={i}
                  isSelected={formData.reportType === type.value}
                  onSelect={() =>
                    !type.disabled &&
                    setFormData((p) => ({ ...p, reportType: type.value }))
                  }
                  onPreview={() => {
                    setPdfError(false);
                    setPdfLoading(true);
                    setPreviewPdf(type.pdfPreview);
                  }}
                />
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-1 pb-1">
            <Button type="submit" disabled={!canSubmit} className="flex-1">
              {creating ? "Creating..." : "Create Class"}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      {/* PDF Preview Modal */}
      {previewPdf && (
        <PdfPreviewModal
          url={previewPdf}
          loading={pdfLoading}
          error={pdfError}
          onLoad={() => setPdfLoading(false)}
          onError={() => {
            setPdfLoading(false);
            setPdfError(true);
          }}
          onClose={() => {
            setPreviewPdf(null);
            setPdfError(false);
            setPdfLoading(false);
          }}
        />
      )}
    </>
  );
}

function ReportTypeRow({ type, index, isSelected, onSelect, onPreview }) {
  return (
    <div
      role={type.disabled ? undefined : "button"}
      tabIndex={type.disabled ? undefined : 0}
      aria-pressed={isSelected}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (!type.disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onSelect();
        }
      }}
      className={[
        "type-row-enter w-full text-left rounded-xl border px-4 py-3",
        "transition-all duration-150 flex items-start gap-3",
        type.disabled
          ? "opacity-50 cursor-not-allowed border-border bg-muted/20"
          : isSelected
            ? "border-primary bg-primary/5 shadow-sm cursor-pointer"
            : "border-border hover:border-muted-foreground/40 hover:bg-muted/30 cursor-pointer",
      ].join(" ")}
      style={{ animationDelay: `${index * 40}ms` }}
    >
      {/* Selection indicator */}
      <div
        className={[
          "mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors",
          type.disabled
            ? "border-muted-foreground/30"
            : isSelected
              ? "border-primary bg-primary"
              : "border-muted-foreground/40",
        ].join(" ")}
      >
        {isSelected && !type.disabled && (
          <LucideCheck size={9} className="text-primary-foreground" />
        )}
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`text-sm font-medium leading-tight ${
              type.disabled ? "text-muted-foreground" : "text-foreground"
            }`}
          >
            {type.label}
          </span>
          <span className="text-xs text-muted-foreground bg-muted rounded px-1.5 py-0.5 leading-none">
            {type.subtitle}
          </span>
          {type.disabled && (
            <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground/70 bg-muted/80 rounded px-1.5 py-0.5 leading-none">
              <LucideClock size={10} />
              Coming soon
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
          {type.description}
        </p>
      </div>

      {/* Preview button */}
      {type.pdfPreview && !type.disabled && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onPreview();
          }}
          title="Preview this report card"
          className="shrink-0 mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <LucideEye size={14} />
        </button>
      )}
    </div>
  );
}

function PdfPreviewModal({ url, loading, error, onLoad, onError, onClose }) {
  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-6"
      style={{ zIndex: 60 }}
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col overflow-hidden"
        style={{ height: "82vh" }}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border shrink-0">
          <p className="text-sm font-medium text-foreground">
            Report Card Preview
          </p>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <LucideX size={15} />
          </button>
        </div>
        <div className="flex-1 overflow-hidden relative">
          {loading && !error && (
            <div className="absolute inset-0 flex items-center justify-center bg-muted/30 z-10">
              <p className="text-sm text-muted-foreground">Loading preview...</p>
            </div>
          )}
          {error ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-5 py-4 text-center max-w-xs">
                <p className="text-sm font-medium text-destructive">
                  Preview unavailable
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Could not load the report card preview.
                </p>
              </div>
            </div>
          ) : (
            <iframe
              key={url}
              src={url}
              className="w-full h-full border-0"
              title="Report card preview"
              onLoad={onLoad}
              onError={onError}
            />
          )}
        </div>
      </div>
    </div>
  );
}
